import React, { useState } from 'react'
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, IconButton, Tooltip, Chip } from '@mui/material';
// icons
import EastIcon from '@mui/icons-material/East';
// utils and helpers
import { formatTimestampToWithoutTime } from "utilities/common";
import { getKitVerifications } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import AgGridAccordionTable from "components/tables/AgGridAccordionTable";
import { CircularProgressBar } from "common/progressBar";
import StatusText from "common/component/StatusText";
import { DistributionFilter, DateFilter, StatusFilter } from "common/filters";

const statusOptions = [
  { value: "", label: "All" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
  { value: "Pending", label: "Pending" },
]

const DriverKitVerification = () => {
  const navigate = useNavigate();

  const [customFilters, setCustomFilters] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);
  const [resetDateFilter, setResetDateFilter] = useState(false);

  const columnDefs = [
    {
      headerName: 'ID Type',
      field: 'distributionType',
      suppressMenu: false,
      minWidth: 400,
      flex: 1.5,
      sortable: false,
      className: 'field-region',
    },
    {
      headerName: 'Overall Status',
      field: 'overallStatus',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-opening-hour',
      cellRenderer: ({ data }) => <StatusText status={data?.overallStatus} />
    },
    {
      headerName: 'Date of Creation',
      field: 'createdDate',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
      valueGetter: ({ data }) => data?.createdDate ? formatTimestampToWithoutTime(Number(data?.createdDate)) : "-",
    },
    {
      headerName: 'Verified Kit status',
      field: 'totalProducts',
      suppressMenu: false,
      flex: 1,
      sortable: false,
      className: 'field-status',
      cellRenderer: ({ data }) => {
        const { verifiedStats } = data || {}
        return <Box>
          <CircularProgressBar value={verifiedStats?.verified} maxValue={verifiedStats?.total} text={(`${verifiedStats?.verified}/${verifiedStats?.total}`)} />
        </Box>
      }
    },
  ];

  const expandedColumnDefs = [
    {
      headerName: 'Regions',
      field: 'region',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 220,
      sortable: false
    },
    {
      headerName: 'Sub Regions',
      field: 'subRegions',
      flex: 1.5,
      minWidth: 240,
      suppressMenu: false,
      sortable: false,
      valueGetter: ({ data }) => {
        return data?.subRegions?.length ? data?.subRegions?.join(", ") : "-"
      }
    },
    {
      headerName: 'Driver Name',
      field: 'driverName',
      flex: 1.5,
      minWidth: 180,
      suppressMenu: false,
      sortable: false,
      cellRenderer: ({ data }) => {
        if (!data) return "-";
        const firstDriverName = data?.drivers?.[0];
        if (data?.drivers?.length <= 1) return firstDriverName;

        return (
          <Tooltip
            title={
              <>
                {data?.drivers?.map(driver => (
                  <Typography key={driver} variant="caption" component="p" fontWeight={500} textAlign="left">
                    {driver}
                  </Typography>
                ))}
              </>
            } arrow>
            <span className='table-row-chip chip-cus' style={{ cursor: "pointer" }}>
              {firstDriverName}
              <Chip
                key={data?._id || ""}
                label={`+${data?.drivers?.length - 1}`}
                style={{ p: 0 }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: 'Discrepancy Count',
      field: 'discrepancyCount',
      flex: 1.5,
      minWidth: 180,
      suppressMenu: false,
      sortable: false,
    },
    {
      headerName: 'Status',
      flex: 1.5,
      minWidth: 200,
      field: 'status',
      suppressMenu: false,
      sortable: false,
      cellRenderer: ({ data }) => <StatusText status={data?.status} />,
    },
    {
      headerName: '',
      field: 'actions',
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      minWidth: 100,
      flex: 1,
      // pinned: 'right',
      className: 'field-actions',
      cellRenderer: (props) => {
        const { data = {}, context = {} } = props || {};
        const { distributionStatus } = context.parentData || {};

        if (!data?.drivers?.length || !["freeze", "distributed"].includes(distributionStatus)) return;

        return <Box display="flex" flexDirection="row" justifyContent="end" gap={0.5}>
          <IconButton onClick={() => navigate(`/hyperwolf/driver-kit-verification/drivers/${data?.distributionId}?region=${data?.regionId}`)} size='small'>
            <EastIcon width='16px' height='16px' />
          </IconButton>
        </Box>
      },
    },
  ];

  const handleChange = async (e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleDateChange = async (values) => {
    setCustomFilters(prev => ({ ...prev, ...values }));
    setRefreshTable(prev => !prev)
  }

  const handleClearFilters = () => {
    setCustomFilters({ search: customFilters.search || "" });
    setRefreshTable(prev => !prev)
    setResetDateFilter(prev => !prev)
  }

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">Driver Kit Verification</Typography>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={3} xxxl={2}>
              <DistributionFilter
                value={customFilters?.distributionType}
                handleChange={handleChange}
                placeholder="Distribution Type"
                options={statusOptions}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <StatusFilter
                value={customFilters?.status}
                handleChange={handleChange}
                placeholder="Overall Status"
                options={statusOptions}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2} className="btn-filter-icon">
              <DateFilter
                handleChange={handleDateChange}
                resetDateFilter={resetDateFilter}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Products listing */}
        <div className="cus-four-columns">
          <AgGridAccordionTable
            tableName="Kit verification"
            columnDefs={columnDefs}
            expandedColumnDefs={expandedColumnDefs}
            getDataMethod={getKitVerifications}
            customFilters={customFilters}
            refreshTable={refreshTable}
            onChildCellClicked={(props) => {
              const data = props?.data || {};
              const { distributionStatus } = props?.context?.parentData || {};
              // Prevent row click if click originated from the actions column
              if (props?.column && (["actions"].includes(props.column.getColId()))) {
                return;
              }
              if (!data?.drivers?.length || !["freeze", "distributed"].includes(distributionStatus)) return;
              navigate(`/hyperwolf/driver-kit-verification/drivers/${data?.distributionId}?region=${data?.regionId}`)
            }}
          />
        </div>
      </Box>
    </CommonLayout>
  )
}

export default DriverKitVerification
